import React, { useEffect, useState } from 'react';
import { PlayCircle, X, Loader2, Clock, Film } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { PAGE_COPY_DEFAULTS } from '../../lib/pageCopyDefaults';

const API = process.env.REACT_APP_BACKEND_URL;
export const fmtDur = (min) => (min >= 60 ? `${Math.floor(min / 60)} h ${min % 60} min` : `${min} min`);
export const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** In-app player: streams the cloud recording through our backend (short-lived signed URL, no Zoom link exposed). */
export function RecordingPlayer({ rec, onClose }) {
  const [src, setSrc] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/api/recordings/${rec.id}/play`).then((r) => setSrc(`${API}${r.data.url}`)).catch((e) => setErr(e?.response?.data?.detail || 'This recording is not available'));
  }, [rec.id]);
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="recording-player">
      <div className="bg-[#0b1220] text-white rounded-none sm:rounded-3xl w-full max-w-5xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10">
          <div className="min-w-0"><p className="font-[Manrope] font-extrabold truncate" data-testid="player-title">{rec.title}</p><p className="text-[11px] text-white/60">{fmtDate(rec.recording_start)} · {fmtDur(rec.duration)}</p></div>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white" data-testid="player-close"><X size={20} /></button>
        </div>
        <div className="aspect-video bg-black flex items-center justify-center">
          {err ? <p className="text-sm text-rose-300 px-6 text-center" data-testid="player-error">{err}</p> : !src ? <Loader2 size={28} className="animate-spin text-teal-300" /> : (
            <video src={src} controls controlsList="nodownload" autoPlay playsInline className="w-full h-full" data-testid="player-video" onError={() => setErr('The video could not be loaded — try again in a minute.')} />
          )}
        </div>
      </div>
    </div>
  );
}

/** Students → Group Classes → Recordings: only what the admin switched on. */
export function RecordingsList({ copy }) {
  const c = copy || PAGE_COPY_DEFAULTS.group_classes;
  const [items, setItems] = useState(null);
  const [playing, setPlaying] = useState(null);
  useEffect(() => { api.get('/api/recordings').then((r) => setItems(r.data.items || [])).catch(() => { setItems([]); toast.error('Could not load recordings'); }); }, []);
  if (!items) return <p className="text-slate-400 py-10 text-center inline-flex items-center gap-2 w-full justify-center" data-testid="recordings-loading"><Loader2 size={16} className="animate-spin" /> Loading…</p>;
  return (
    <div className="space-y-3" data-testid="recordings-tab">
      <p className="text-sm text-slate-500 max-w-2xl">{c.recordings_intro}</p>
      {items.length === 0 ? <p className="text-sm text-slate-500 rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center" data-testid="recordings-empty">{c.recordings_empty}</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((r) => (
            <button key={r.id} type="button" onClick={() => setPlaying(r)} className="text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-teal-400 hover:shadow transition flex items-start gap-3" data-testid={`recording-${r.id}`}>
              <div className="w-12 h-12 rounded-xl bg-teal-700 text-white flex items-center justify-center shrink-0"><PlayCircle size={24} /></div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 truncate">{r.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-2 flex-wrap"><span className="inline-flex items-center gap-1"><Clock size={11} /> {fmtDate(r.recording_start)}</span><span className="inline-flex items-center gap-1"><Film size={11} /> {fmtDur(r.duration)}</span>{r.kind === 'call' && <span className="rounded bg-indigo-100 text-indigo-800 px-1.5 py-0.5 text-[10px] font-bold">1-on-1</span>}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {playing && <RecordingPlayer rec={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
}
